import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { BiX, BiMenu, BiChevronDown } from "react-icons/bi";
import { BsArrowRight } from "react-icons/bs";
import "./navbar.css";
import logoImg from "../../assets/Logo.png";

const serviceNavItems = [
  {
    id: "service-1",
    path: "/services/finance",
    title: "Financial Consultancy & Advisory",
    desc: "Project finance & due diligence",
  },
  {
    id: "service-2",
    path: "/services/project-development",
    title: "Technical Consultancy & Engineering",
    desc: "Feasibility, grid & engineering studies",
  },
  {
    id: "service-3",
    path: "/services/epc-coordination",
    title: "Turnkey EPC & Project Execution",
    desc: "Design, planning & commissioning",
  },
  {
    id: "service-4",
    path: "/services/emerging",
    title: "BESS, Hybrid & Green Hydrogen",
    desc: "Battery storage & hybrid solutions",
  },
  {
    id: "service-5",
    path: "/services/residential-solar-installation",
    title: "Residential Solar Installation",
    desc: "Turnkey rooftop and ground mounted systems",
  },
  {
    id: "service-6",
    path: "/services/emerging",
    title: "Clean Energy Storage & Hydrogen",
    desc: "Integrated storage & clean tech solutions",
  },
];

const Navbar = () => {
  const [showNavbar, setShowNavbar] = useState(true);
  const [isTop, setIsTop] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  const dropdownTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePageNav = (path, scrollToId = null) => {
    setIsMenuOpen(false);
    setIsServicesDropdownOpen(false);
    setIsMobileServicesOpen(false);

    if (scrollToId) {
      if (location.pathname === "/") {
        const el = document.getElementById(scrollToId);
        if (el) {
          const offset = 100;
          const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top: pos, behavior: "smooth" });
          return;
        }
      }
      navigate("/", { state: { scrollTo: scrollToId } });
      return;
    }
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleMouseEnterServices = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsServicesDropdownOpen(true);
  };

  const handleMouseLeaveServices = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 150);
  };

  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      setIsTop(currentScrollY < 50);
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);
        setIsServicesDropdownOpen(false);
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", controlNavbar);
    return () => window.removeEventListener("scroll", controlNavbar);
  }, [lastScrollY]);

  return (
    <nav className={`custom-navbar ${showNavbar ? "navbar-visible" : "navbar-hidden"} ${isTop ? "at-top" : "scrolled"}`}>
      <div className="navbar-main-container">
        {/* Floating Pill for Desktop */}
        <div className="navbar-floating-pill d-none d-lg-flex">
          <Link to="/" onClick={() => handlePageNav("/")} className="navbar-logo-wrapper">
            <img src={logoImg} alt="Dazbog" className="logo-img" />
          </Link>

          <div className="navbar-links-center">
            <NavLink to="/" className="nav-item-link" onClick={() => handlePageNav("/")}>Home</NavLink>
            <NavLink to="/about" className="nav-item-link" onClick={() => handlePageNav("/about")}>About</NavLink>

            {/* Services Dropdown Item */}
            <div 
              className={`nav-dropdown-wrapper ${isServicesDropdownOpen ? "open" : ""}`}
              onMouseEnter={handleMouseEnterServices}
              onMouseLeave={handleMouseLeaveServices}
            >
              <button 
                type="button"
                className="nav-item-link nav-dropdown-btn"
                onClick={() => handlePageNav("/services")}
              >
                <span>Services</span>
                <BiChevronDown className={`dropdown-chevron ${isServicesDropdownOpen ? "rotated" : ""}`} />
              </button>

              {/* Desktop Dropdown Menu */}
              <div className={`nav-dropdown-menu ${isServicesDropdownOpen ? "show" : ""}`}>
                <div className="nav-dropdown-header">
                  <span className="dropdown-section-tag">OUR SOLUTIONS</span>
                </div>
                <div className="nav-dropdown-list">
                  {serviceNavItems.map((service, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="nav-dropdown-item"
                      onClick={() => handlePageNav(service.path)}
                    >
                      <div className="dropdown-item-text">
                        <span className="dropdown-item-title">{service.title}</span>
                        <span className="dropdown-item-desc">{service.desc}</span>
                      </div>
                      <BsArrowRight className="dropdown-item-arrow" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/projects" className="nav-item-link" onClick={() => handlePageNav("/projects")}>Projects</NavLink>
            <NavLink to="/contact" className="nav-item-link" onClick={() => handlePageNav("/contact")}>Contact</NavLink>
          </div>

          <div className="navbar-cta-right">
            <button className="navbar-contact-btn" onClick={() => handlePageNav("/contact")}>
              Contact Us
            </button>
          </div>
        </div>

        {/* Standard Mobile Navbar */}
        <div className="mobile-navbar-row d-flex d-lg-none w-100 justify-content-between align-items-center">
          <Link to="/" onClick={() => handlePageNav("/")}>
            <img src={logoImg} alt="Dazbog" className="logo-img" />
          </Link>
          <button className="navbar-toggler border-0" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <BiX size={35} /> : <BiMenu size={35} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div className={`mobile-drawer-wrapper d-lg-none ${isMenuOpen ? "show" : ""}`}>
        <div className="mobile-drawer-header">
          <img src={logoImg} alt="Dazbog" className="logo-img" />
          <BiX size={35} onClick={() => setIsMenuOpen(false)} />
        </div>
        <div className="mobile-inner-content">
          <NavLink to="/" onClick={() => handlePageNav("/")}>Home</NavLink>
          <NavLink to="/about" onClick={() => handlePageNav("/about")}>About Us</NavLink>
          
          {/* Mobile Services Accordion */}
          <div className="mobile-accordion-wrapper">
            <div 
              className="mobile-accordion-toggle"
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
            >
              <span>Services</span>
              <BiChevronDown className={`mobile-accordion-chevron ${isMobileServicesOpen ? "rotated" : ""}`} />
            </div>

            {isMobileServicesOpen && (
              <div className="mobile-accordion-list">
                <button 
                  type="button" 
                  className="mobile-subitem-link mobile-all-services"
                  onClick={() => handlePageNav("/services")}
                >
                  All Services Overview
                </button>
                {serviceNavItems.map((service, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="mobile-subitem-link"
                    onClick={() => handlePageNav(service.path)}
                  >
                    <span>{service.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <NavLink to="/projects" onClick={() => handlePageNav("/projects")}>Projects</NavLink>
          <NavLink to="/contact" onClick={() => handlePageNav("/contact")}>Contact</NavLink>
          
          <div className="mt-4 pb-4">
            <button 
              className="navbar-contact-btn w-100" 
              onClick={() => handlePageNav("/contact")}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;